/**
 * Assinaturas em tempo real do Mural de Recados.
 *
 * `useMuralOpen` é o mínimo — só o que está em aberto. A Sidebar usa ele pro
 * badge, então ele precisa ser barato: uma consulta de igualdade, sem
 * histórico e sem os resolvidos.
 *
 * `useMuralBoard` acrescenta a coluna Resolvido de hoje e o relógio da virada
 * do dia. Só a página do mural precisa disso.
 */
import { useEffect, useMemo, useState } from 'react';
import { MuralNoteService, brasiliaDayKey } from '@/services/firebase/muralNotes';
import { Role } from '@/types/auth';
import type { MuralNote } from '@/types/models';

/** Papéis que enxergam o mural. Espelha as regras do Firestore. */
export const MURAL_ROLES: Role[] = [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA];

export function canSeeMural(role?: Role): boolean {
  return !!role && MURAL_ROLES.includes(role);
}

/** Recados em aberto (anotado + aguardando), de qualquer dia. */
export function useMuralOpen(clinicId?: string | null, role?: Role) {
  const [notes, setNotes] = useState<MuralNote[]>([]);
  const [loading, setLoading] = useState(true);

  const enabled = !!clinicId && canSeeMural(role);

  useEffect(() => {
    if (!enabled || !clinicId) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = MuralNoteService.subscribeOpen(
      clinicId,
      (list) => {
        setNotes(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [clinicId, enabled]);

  const anotado = useMemo(() => notes.filter((n) => n.status === 'anotado'), [notes]);
  const aguardando = useMemo(() => notes.filter((n) => n.status === 'aguardando'), [notes]);

  return {
    notes,
    anotado,
    aguardando,
    /**
     * O número do badge da sidebar conta SÓ `anotado` — o que precisa de ação
     * de alguém. Somar `aguardando` faria o badge nunca zerar, e badge que
     * nunca zera o olho para de ver.
     */
    badgeCount: anotado.length,
    loading,
  };
}

/**
 * Mural completo: abertos + resolvidos de hoje. O `dayKey` é reavaliado de
 * minuto em minuto pra que uma aba deixada aberta a noite inteira troque de
 * dia sozinha (a coluna Resolvido esvazia na virada, sem F5).
 */
export function useMuralBoard(clinicId?: string | null, role?: Role) {
  const open = useMuralOpen(clinicId, role);
  const [dayKey, setDayKey] = useState(() => brasiliaDayKey());
  const [resolvedToday, setResolvedToday] = useState<MuralNote[]>([]);

  useEffect(() => {
    const t = setInterval(() => {
      const next = brasiliaDayKey();
      setDayKey((prev) => (prev === next ? prev : next));
    }, 60_000);
    return () => clearInterval(t);
  }, []);

  const enabled = !!clinicId && canSeeMural(role);

  useEffect(() => {
    if (!enabled || !clinicId) {
      setResolvedToday([]);
      return;
    }
    return MuralNoteService.subscribeResolvedToday(clinicId, dayKey, setResolvedToday);
  }, [clinicId, dayKey, enabled]);

  return { ...open, resolvedToday, dayKey };
}
